import React, { useCallback, useEffect, useRef, useState } from 'react';
import SEO from '../components/SEO';
import { useTranslation } from '../hooks/useTranslation';
import DiamondVPNService from '../services/api';
import '../styles/news.css';

const PAGE_SIZE = 8;

const News = () => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    setError(null);
    try {
      const data = await DiamondVPNService.getNews(offset, PAGE_SIZE);
      const nextItems = Array.isArray(data.items) ? data.items : [];
      setItems((prev) => [...prev, ...nextItems]);
      setOffset((prev) => prev + nextItems.length);
      setHasMore(nextItems.length === PAGE_SIZE);
    } catch (e) {
      setError(e?.message || 'Failed to load news');
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, offset]);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const locale = (i18n && i18n.language) || 'ru';
  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="news-page">
      <SEO
        title={`${t('news.title')} - Diamond VPN`}
        description={t('news.subtitle')}
        ogImage="https://diamondvpn.online/preview.jpg"
      />

      <div className="news-container" data-aos="fade-up">
        <div className="news-header">
          <h1 className="news-title">{t('news.title')}</h1>
          <p className="news-subtitle">{t('news.subtitle')}</p>
        </div>

        {items.length === 0 && !loading && !error && (
          <div className="news-empty">{t('news.empty')}</div>
        )}

        <div className="news-list">
          {items.map((item) => (
            <article className="news-card" key={item.id}>
              <div className="news-card-top">
                <span className="news-date">{formatDate(item.published_at)}</span>
              </div>
              <h2 className="news-card-title">{item.title}</h2>
              <div className="news-card-content">{item.content}</div>
            </article>
          ))}
        </div>

        {error && <div className="news-error">{error}</div>}

        <div ref={sentinelRef} className="news-sentinel" />
        {loading && <div className="news-loading">{t('common.loading')}</div>}
        {!hasMore && items.length > 0 && (
          <div className="news-end">{t('news.end')}</div>
        )}
      </div>
    </div>
  );
};

export default News;
